import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher } from '../game/types';
import { AudioManager } from '../audio/AudioManager';

export class ResearcherPanel {
  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onSelect: (id: string) => void
  ) {
    this.update();
  }

  selectNextResearcher(): void {
    const researchers = this.gameState.getResearchers();
    if (researchers.length === 0) return;

    const selected = this.gameState.getSelectedEntity();
    if (selected.type === 'researcher' && selected.id) {
      const currentIndex = researchers.findIndex(r => r.id === selected.id);
      if (currentIndex >= 0) {
        const nextIndex = (currentIndex + 1) % researchers.length;
        this.onSelect(researchers[nextIndex].id);
      } else {
        // Current selection not found, select first
        this.onSelect(researchers[0].id);
      }
    } else {
      // No researcher selected, select first
      this.onSelect(researchers[0].id);
    }
  }

  update(): void {
    const researchers = this.gameState.getResearchers();
    const selected = this.gameState.getSelectedEntity();
    const strings = this.gameData.uiStrings;

    // Only update if structure changed, or update content without recreating
    const header = this.container.querySelector('.researcher-panel-header');
    const state = this.gameState.getState();
    const maxResearchers = this.gameData.gameConfig.max_researchers;
    const currentCount = state.researchers.length;
    const headerText = `${strings.labels?.researcher_name || 'Researchers'} (${currentCount}/${maxResearchers})`;
    
    if (!header) {
      // Initial render
      this.container.innerHTML = `
        <div class="researcher-panel-header">${headerText}</div>
        <div class="researcher-cards-container"></div>
      `;
      
      // Attach click handlers only once using event delegation
      this.container.addEventListener('click', (e) => {
        const card = (e.target as HTMLElement).closest('[data-researcher-id]');
        if (card) {
          const id = card.getAttribute('data-researcher-id');
          if (id) {
            e.stopPropagation();
            e.preventDefault();
            AudioManager.getInstance().playClick();
            this.onSelect(id);
          }
        }
      });
    } else {
      // Update header text if it exists
      header.textContent = headerText;
    }

    const cardsContainer = this.container.querySelector('.researcher-cards-container')!;
    
    // Update existing cards or create new ones
    researchers.forEach((researcher, index) => {
      const existingCard = cardsContainer.querySelector(`[data-researcher-id="${researcher.id}"]`) as HTMLElement;
      const isSelected = selected.type === 'researcher' && selected.id === researcher.id;
      
      if (existingCard) {
        // Update existing card
        const hasDamageEffect = researcher.sanityDamageEffect === true;
        existingCard.className = `researcher-card ${isSelected ? 'selected' : ''} ${hasDamageEffect ? 'sanity-damage-effect' : ''}`;
        
        // Clear the damage effect flag after applying
        if (hasDamageEffect) {
          researcher.sanityDamageEffect = false;
          // Remove the class after animation completes
          setTimeout(() => {
            existingCard.classList.remove('sanity-damage-effect');
          }, 500);
        }
        
        existingCard.querySelector('.researcher-name')!.textContent = researcher.data.name;
        existingCard.querySelector('.researcher-location')!.textContent = strings.locations?.[researcher.location || 'control_room'] || researcher.location || 'control_room';
        const actionName = researcher.currentAction 
          ? strings.status?.[researcher.currentAction.toLowerCase()] || researcher.currentAction
          : strings.status?.idle || 'Idle';
        existingCard.querySelector('.researcher-action')!.textContent = actionName;
        
        // Update sanity display with color and warning (round up for display)
        const hasMemeticContagion = this.hasMemeticContagion(researcher);
        const displayedSanity = Math.ceil(researcher.currentSanity);
        const sanityText = `${strings.labels?.sanity || 'Sanity'}: ${displayedSanity}/${researcher.data.sanity}`;
        const warningSymbol = hasMemeticContagion ? ' ⚠️' : '';
        const sanityClass = hasMemeticContagion ? 'researcher-sanity sanity-low' : 'researcher-sanity';
        
        let sanityElement = existingCard.querySelector('.researcher-sanity') as HTMLElement;
        if (!sanityElement) {
          // Create sanity element if it doesn't exist
          sanityElement = document.createElement('div');
          existingCard.appendChild(sanityElement);
        }
        sanityElement.textContent = sanityText + warningSymbol;
        sanityElement.className = sanityClass;
        
        // Update progress bar
        const progressBar = existingCard.querySelector('.progress-bar');
        if (researcher.currentAction && researcher.actionTotalTime > 0) {
          const progress = (1 - researcher.actionTimer / researcher.actionTotalTime) * 100;
          if (!progressBar) {
            existingCard.insertAdjacentHTML('beforeend', `
              <div class="progress-bar">
                <div class="progress-fill" style="width: ${progress}%"></div>
              </div>
            `);
          } else {
            const fill = progressBar.querySelector('.progress-fill') as HTMLElement;
            if (fill) fill.style.width = `${progress}%`;
          }
        } else if (progressBar) {
          progressBar.remove();
        }
      } else {
        // Create new card
        cardsContainer.insertAdjacentHTML('beforeend', this.renderResearcher(researcher, isSelected));
      }
    });
    
    // Remove cards for researchers that no longer exist
    const existingCards = cardsContainer.querySelectorAll('[data-researcher-id]');
    existingCards.forEach(card => {
      const id = card.getAttribute('data-researcher-id');
      if (!researchers.find(r => r.id === id)) {
        card.remove();
      }
    });
  }

  private renderResearcher(researcher: Researcher, isSelected: boolean): string {
    const strings = this.gameData.uiStrings;
    const state = this.gameState.getState();
    const location = researcher.location || 'control_room';
    const locationName = strings.locations?.[location] || location;
    const actionName = researcher.currentAction 
      ? strings.status?.[researcher.currentAction.toLowerCase()] || researcher.currentAction
      : strings.status?.idle || 'Idle';
    
    const progress = researcher.actionTotalTime > 0 
      ? (1 - researcher.actionTimer / researcher.actionTotalTime) * 100 
      : 0;

    const hasMemeticContagion = this.hasMemeticContagion(researcher);
    const displayedSanity = Math.ceil(researcher.currentSanity);
    const sanityText = `${strings.labels?.sanity || 'Sanity'}: ${displayedSanity}/${researcher.data.sanity}`;
    const warningSymbol = hasMemeticContagion ? ' ⚠️' : '';
    const sanityClass = hasMemeticContagion ? 'researcher-sanity sanity-low' : 'researcher-sanity';

    return `
      <div class="researcher-card ${isSelected ? 'selected' : ''}" data-researcher-id="${researcher.id}">
        <div class="researcher-name">${researcher.data.name}</div>
        <div class="researcher-location">${locationName}</div>
        <div class="researcher-action">${actionName}</div>
        <div class="${sanityClass}">${sanityText}${warningSymbol}</div>
        ${researcher.currentAction && researcher.actionTotalTime > 0 ? `
          <div class="progress-bar">
            <div class="progress-fill" style="width: ${progress}%"></div>
          </div>
        ` : ''}
      </div>
    `;
  }

  private hasMemeticContagion(researcher: Researcher): boolean {
    const maxSanity = researcher.data.sanity;
    const currentSanity = researcher.currentSanity;
    return currentSanity < maxSanity / 2;
  }
}


